// Motivational popup — appears in-app after user actions trigger eligibility
// in the server's rules engine. Auto-dismisses after 8s with a slow fade.
// Tap → expands with a "thanks, keep going" button and a small
// "show me less of this kind" link that adds a 30-day cooldown.
//
// Render rules:
//  - aria-live="polite" so screen readers announce without interruption
//  - Esc dismisses (we don't trap focus)
//  - prefers-reduced-motion: skips the fade and slide via motion lib
//
// Wiring lives in usePopup() — placed once at the AppShell layer.

import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import type { PopupWire } from '@shared/types';
import { apiPost } from '../../lib/api';
import { Button } from '../ui/Button';
import { EASE_OUT, useReducedMotion } from '../../lib/motion';

interface MotivationalToastProps {
  popup: PopupWire | null;
  onDismiss: () => void;
  /**
   * Phase B §P10.6 — honest-day suppression. When true (the user is
   * over today's target with margin), the toast does not render at
   * all. This is in ADDITION to the server's existing high-SOWS
   * suppression; this client-side gate fires on the day a setback
   * happens, before the next server-side rule cycle has refreshed.
   */
  suppress?: boolean;
}

const AUTO_DISMISS_MS = 8000;

export function MotivationalToast({ popup, onDismiss, suppress }: MotivationalToastProps) {
  const [expanded, setExpanded] = useState(false);
  const reduce = useReducedMotion();

  // Effects run regardless of suppress so React rules-of-hooks holds
  // even if the parent toggles `suppress` while a popup is mid-flight.
  // The effects themselves short-circuit on `suppress` so the auto-
  // dismiss timer and keydown listener don't fight a noop render.
  // Reset the expanded state whenever a new popup arrives (or it
  // clears). Kept separate from the auto-dismiss timer so the timer
  // effect can depend on `expanded` without this reset re-collapsing
  // the toast on every expand.
  useEffect(() => {
    setExpanded(false);
  }, [popup]);

  useEffect(() => {
    if (suppress) return;
    if (!popup) return;
    if (expanded) return; // user opened it, hold open — cancels the pending timer
    const t = window.setTimeout(onDismiss, AUTO_DISMISS_MS);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popup, suppress, expanded]);

  useEffect(() => {
    if (suppress) return;
    if (!popup) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onDismiss();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [popup, onDismiss, suppress]);

  // Phase B §P10.6 — honest-day suppression: render nothing on a
  // day the user logged over today's target. Support surfaces
  // (crisis, urge-surf, hold suggestion) are unaffected; this gate
  // only silences the motivational popup specifically.
  if (suppress) return null;

  async function dismiss(showLess: boolean) {
    try {
      if (popup) await apiPost(`/api/popup/${popup.id}/dismissed`, { showLess });
    } catch {
      // silent — non-blocking
    }
    onDismiss();
  }

  return (
    <AnimatePresence>
      {popup ? (
        <motion.div
          role="status"
          aria-live="polite"
          className="motivational-toast"
          initial={reduce ? false : { opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduce ? { opacity: 0 } : { opacity: 0, y: -12 }}
          transition={{ duration: 0.24, ease: EASE_OUT }}
          onClick={() => setExpanded((s) => !s)}
        >
          <div className="motivational-toast-body">
            <p className="motivational-toast-text">{popup.text}</p>
            {expanded ? (
              <div
                className="row"
                style={{ justifyContent: 'space-between', marginBlockStart: 'var(--space-3)' }}
                onClick={(e) => e.stopPropagation()}
              >
                <Button variant="secondary" size="md" onClick={() => void dismiss(false)}>
                  Thanks, keep going
                </Button>
                <button
                  type="button"
                  className="motivational-toast-less"
                  onClick={() => void dismiss(true)}
                >
                  Show me less of this
                </button>
              </div>
            ) : null}
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
